/**
 * Analytics-Events (GA4 über gtag.js)
 *
 * Einziger Ort, an dem Events rausgehen. Zwei Wege:
 *
 * 1. Deklarativ im Template — ein Klick auf ein Element mit [data-track]
 *    schickt das Event, alle weiteren data-track-* werden zu Parametern:
 *
 *      <a data-track="social_click" data-track-network="instagram">
 *      → gtag('event', 'social_click', { network: 'instagram' })
 *
 * 2. Aus einem JS-Modul — track('video_start', { method: 'click_sound' }).
 *
 * Ohne gtag (Tracking-Blocker, lokale Umgebung) passiert nichts.
 *
 * ⚠️ Custom-Parameter müssen in GA4 unter Admin → Benutzerdefinierte
 * Definitionen als Dimension registriert werden, sonst tauchen sie in
 * keinem Report auf.
 */

/**
 * Leere Werte fliegen raus, damit in GA4 keine "(not set)"-Zeilen entstehen.
 */
const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  )

export const track = (name, params = {}) => {
  if (!name || typeof window.gtag !== 'function') return

  window.gtag('event', name, clean(params))
}

/**
 * Events, die pro Seitenaufruf nur einmal zählen sollen (Kapitel-Sichtungen).
 */
const fired = new Set()

export const trackOnce = (key, name, params = {}) => {
  if (fired.has(key)) return

  fired.add(key)
  track(name, params)
}

/**
 * data-track-ngo-name="…" → { ngo_name: '…' }
 */
const paramsFromDataset = (dataset) =>
  Object.entries(dataset)
    .filter(([key]) => key.startsWith('track') && key !== 'track')
    .reduce((params, [key, value]) => {
      const name = key
        .slice(5)
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .toLowerCase()

      return { ...params, [name]: value }
    }, {})

/**
 * Deklarative Klick-Events — delegiert, damit auch nachgeladene Teaser zählen.
 */
document.addEventListener('click', (event) => {
  const element = event.target.closest('[data-track]')
  if (!element) return

  track(element.dataset.track, paramsFromDataset(element.dataset))
})

/**
 * Kapitel-Sichtung: welcher Abschnitt des One-Pagers wird überhaupt erreicht.
 * Die GA4-Standardmessung feuert erst bei 90 % Scrolltiefe und ist damit für
 * eine lange Seite zu grob.
 */
const chapters = [...document.querySelectorAll('[data-navi-anchor]')]

if (chapters.length) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((entry) => entry.isIntersecting)
        .forEach(({ target }) =>
          trackOnce(`chapter:${target.id}`, 'chapter_view', {
            chapter_id: target.id,
            chapter_label: target.dataset.chapterLabel,
            chapter_index: target.dataset.chapterIndex,
          })
        )
    },
    { threshold: 0.5 }
  )

  chapters.forEach((chapter) => observer.observe(chapter))
}

/**
 * Threat-Akkordeon: toggle.js schickt toggle:global auch bei resize und load,
 * darum wird hier der Zustandswechsel gemerkt statt jedes Event zu zählen.
 */
const openThreats = new WeakSet()

window.addEventListener('toggle:global', ({ detail }) => {
  const parent = detail?.toggleParent
  const word = parent?.dataset.trackThreatWord

  if (!word) return

  if (!detail.isToggled) {
    openThreats.delete(parent)
    return
  }

  if (openThreats.has(parent)) return

  openThreats.add(parent)
  track('threat_open', { threat_word: word })
})

/**
 * Mobile-Menü (overlay.js dispatcht das Event).
 */
window.addEventListener('overlay:opened', ({ detail }) => {
  if (detail?.key === 'menu') track('menu_open')
})
